/**
 * AdminTracker — 全链路订单流程追踪器
 * 输入订单号，时间轴展示从询盘→报价→生产→物流的完整历程
 * 支持附件上传与查看
 */
import { useState, useRef } from 'react';
import {
  Search, MessageSquare, FileText, Wrench, Truck,
  Paperclip, Upload, X, ExternalLink, CheckCircle2,
  Clock, Package2, AlertCircle, Loader2, ChevronDown, ChevronUp, Trash2,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

const ORANGE    = 'oklch(0.66 0.19 42)';
const CARD_BG   = '#FFFFFF';
const HEADER_BG = '#F0F2F5';

// ── 类型定义 ──────────────────────────────────────────
interface RawOrder {
  id: string; order_no: string; order_status: string;
  payment_status: string; total_amount: number | null;
  created_at: string; inquiry_id: string | null;
}

interface TrackerResult {
  order: {
    id: string; order_no: string; order_status: string;
    payment_status: string; total_amount: number | null; created_at: string;
  } | null;
  inquiry: {
    id: string; name: string; company: string | null;
    material: string | null; message: string | null;
    quoted_price: number | null; status: string; created_at: string;
    biz_no: string | null;
  };
  productionTasks: Array<{
    id: string; stage: string; tech_notes: string | null;
    created_at: string; updated_at: string;
  }>;
  logistics: Array<{
    id: string; carrier: string | null; tracking_no: string | null;
    shipped_at: string | null; estimated_arrival: string | null; created_at: string;
  }>;
  attachments: Array<{
    id: string; file_type: string; file_name: string;
    file_url: string; file_size_kb: number | null;
    uploaded_by: string | null; notes: string | null; created_at: string;
  }>;
  orderItems: Array<{
    id: string; material_code: string; specifications: string | null;
    quantity_kg: number | null; unit_price: number | null; total_amount: number | null;
  }>;
}

const STAGE_LABEL: Record<string, { label: string; color: string }> = {
  scheduling:   { label: '排产中',    color: '#6366F1' },
  raw_material: { label: '原材料准备', color: '#D97706' },
  vulcanizing:  { label: '硫化/切割中', color: 'oklch(0.66 0.19 42)' },
  qc_passed:    { label: '质检完成',   color: '#0891B2' },
  packing:      { label: '包装待发',   color: '#D97706' },
};

const ORDER_STATUS_LABEL: Record<string, string> = {
  new: '新线索', quoted: '已报价', lost: '已流失', pending: '待生产',
  scheduling: '排产中', raw_material: '原材料准备', vulcanizing: '硫化/加工',
  qc_passed: '质检完成', packing: '包装待发', shipped: '已发货', completed: '已完成',
};

const FILE_TYPES = ['客户原始图纸', '核价单', '出库单', '合同', '检测报告', '其他'];

// ── 时间轴节点 ────────────────────────────────────────
function TimelineNode({
  icon: Icon, color, title, subtitle, time, children, defaultOpen = false,
}: {
  icon: React.ElementType; color: string; title: string;
  subtitle?: string; time: string; children?: React.ReactNode; defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="flex gap-4">
      {/* 竖线 + 图标 */}
      <div className="flex flex-col items-center">
        <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 border"
          style={{ background: `${color}18`, borderColor: `${color}50` }}>
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
        <div className="flex-1 w-px mt-1" style={{ background: '#E5E7EB' }} />
      </div>
      {/* 内容 */}
      <div className="flex-1 pb-6">
        <div className="flex items-center justify-between cursor-pointer select-none"
          onClick={() => children && setOpen(o => !o)}>
          <div>
            <p className="text-sm font-bold" style={{ color: '#1A2337' }}>{title}</p>
            {subtitle && <p className="text-xs mt-0.5" style={{ color: '#5A6780' }}>{subtitle}</p>}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-mono" style={{ color: '#5A6780' }}>{new Date(time).toLocaleString('zh-CN')}</span>
            {children && (open ? <ChevronUp className="w-3.5 h-3.5" style={{ color: '#5A6780' }} /> : <ChevronDown className="w-3.5 h-3.5" style={{ color: '#5A6780' }} />)}
          </div>
        </div>
        {open && children && (
          <div className="mt-3 rounded-sm border border-border p-4" style={{ background: '#F7F8FA' }}>
            {children}
          </div>
        )}
      </div>
    </div>
  );
}

// ── 附件上传区 ────────────────────────────────────────
function AttachmentUploader({ orderId, onUploaded }: { orderId: string; onUploaded: () => void }) {
  const [fileType, setFileType] = useState(FILE_TYPES[0]);
  const [notes, setNotes] = useState('');
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const IS: React.CSSProperties = {
    background: '#FFFFFF', border: '1px solid #D0D5E2', color: '#1A2337',
    borderRadius: '4px', padding: '6px 10px', fontSize: '12px', outline: 'none',
  };

  const handleUpload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { setErr('请选择文件'); return; }
    if (file.size > 10 * 1024 * 1024) { setErr('文件不能超过 10MB'); return; }

    setUploading(true); setErr('');
    try {
      // 上传到 Supabase Storage（bucket: order-attachments）
      const ext = file.name.split('.').pop();
      const path = `${orderId}/${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from('order-attachments')
        .upload(path, file, { upsert: false });

      if (upErr) throw new Error(upErr.message);

      const { data: urlData } = supabase.storage.from('order-attachments').getPublicUrl(path);
      const fileUrl = urlData.publicUrl;

      // 写入 order_attachments 表
      const { error: dbErr } = await supabase.from('order_attachments').insert({
        order_id: orderId,
        file_type: fileType,
        file_name: file.name,
        file_url: fileUrl,
        file_size_kb: Math.round(file.size / 1024),
        notes: notes || null,
      });

      if (dbErr) throw new Error(dbErr.message);

      // 重置
      if (fileRef.current) fileRef.current.value = '';
      setNotes('');
      onUploaded();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : '上传失败');
    }
    setUploading(false);
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2 flex-wrap">
        <select value={fileType} onChange={e => setFileType(e.target.value)}
          style={{ ...IS, width: '140px' }}>
          {FILE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.xlsx,.docx"
          style={{ ...IS, flex: 1, minWidth: '180px', padding: '4px 10px' }} />
      </div>
      <div className="flex gap-2">
        <input value={notes} onChange={e => setNotes(e.target.value)}
          placeholder="备注（选填）"
          style={{ ...IS, flex: 1 }} />
        <button onClick={handleUpload} disabled={uploading}
          className="flex items-center gap-1.5 text-xs font-bold px-4 py-1.5 rounded-sm disabled:opacity-60"
          style={{ background: ORANGE, color: '#fff', whiteSpace: 'nowrap' }}>
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
          上传
        </button>
      </div>
      {err && <p className="text-xs" style={{ color: '#F87171' }}>{err}</p>}
    </div>
  );
}

// ── 主组件 ────────────────────────────────────────────
export function TrackerView() {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<TrackerResult | null>(null);
  const [err, setErr] = useState('');

  const [deletingAttId, setDeletingAttId] = useState<string | null>(null);

  const search = async () => {
    if (!query.trim()) return;
    setLoading(true); setErr(''); setResult(null);

    // 1. 首先通过 biz_no 查询线索（线索优先）
    const { data: inqData } = await supabase
      .from('inquiries')
      .select('*')
      .ilike('biz_no', `%${query.trim()}%`)
      .limit(1);

    let inquiry = inqData?.[0] ?? null;

    // 2. 如果线索找到，查询关联订单；否则尝试直接查订单
    let rawOrder: RawOrder | null = null;
    if (inquiry) {
      const { data: ordData } = await supabase
        .from('orders')
        .select('*')
        .eq('order_no', inquiry.biz_no)
        .maybeSingle();
      rawOrder = ordData as RawOrder | null;
    } else {
      // 兼容：直接用订单号搜索
      const { data: ordData } = await supabase
        .from('orders')
        .select('*')
        .ilike('order_no', `%${query.trim()}%`)
        .limit(1);
      if (ordData && ordData.length > 0) {
        rawOrder = ordData[0] as RawOrder;
        if (rawOrder.inquiry_id) {
          const { data: inq2 } = await supabase.from('inquiries').select('*').eq('id', rawOrder.inquiry_id).single();
          inquiry = inq2;
        }
      }
    }

    if (!inquiry && !rawOrder) {
      setErr(`未找到包含"${query}"的业务编号，请检查格式（如 PP-20260101-001）`);
      setLoading(false); return;
    }

    // 3. 如果仍没有线索（纯订单情况），构造最小inquiry
    const finalInquiry = inquiry ?? {
      id: '', name: '—', company: null, material: null, message: null,
      quoted_price: null, status: rawOrder?.order_status ?? '', created_at: rawOrder?.created_at ?? '', biz_no: rawOrder?.order_no ?? null,
    };

    const order = rawOrder ? {
      id: rawOrder.id, order_no: rawOrder.order_no, order_status: rawOrder.order_status,
      payment_status: rawOrder.payment_status, total_amount: rawOrder.total_amount,
      created_at: rawOrder.created_at,
    } : null;

    // 4. 并行查询所有关联数据
    const orderId = order?.id;
    const [taskRes, logRes, attRes, itemRes] = await Promise.all([
      orderId ? supabase.from('production_tasks').select('*').eq('order_id', orderId).order('created_at') : Promise.resolve({ data: [] }),
      orderId ? supabase.from('logistics').select('*').eq('order_id', orderId).order('created_at') : Promise.resolve({ data: [] }),
      orderId ? supabase.from('order_attachments').select('*').eq('order_id', orderId).order('created_at') : Promise.resolve({ data: [] }),
      orderId ? supabase.from('order_items').select('*').eq('order_id', orderId) : Promise.resolve({ data: [] }),
    ]);

    setResult({
      order,
      inquiry: finalInquiry as TrackerResult['inquiry'],
      productionTasks: (taskRes.data ?? []) as TrackerResult['productionTasks'],
      logistics: (logRes.data ?? []) as TrackerResult['logistics'],
      attachments: (attRes.data ?? []) as TrackerResult['attachments'],
      orderItems: (itemRes.data ?? []) as TrackerResult['orderItems'],
    });
    setLoading(false);
  };

  const reloadAttachments = async () => {
    if (!result?.order) return;
    const { data } = await supabase.from('order_attachments').select('*').eq('order_id', result.order.id).order('created_at');
    setResult(r => r ? { ...r, attachments: (data ?? []) as TrackerResult['attachments'] } : r);
  };

  const handleDeleteAttachment = async (att: TrackerResult['attachments'][0]) => {
    if (!confirm(`确认删除附件"${att.file_name}"？`)) return;
    setDeletingAttId(att.id);
    // 从 Storage 删除文件
    const pathMatch = att.file_url.match(/order-attachments\/(.+)$/);
    if (pathMatch) {
      await supabase.storage.from('order-attachments').remove([pathMatch[1]]);
    }
    // 从数据库删除记录
    await supabase.from('order_attachments').delete().eq('id', att.id);
    await reloadAttachments();
    setDeletingAttId(null);
  };

  const statusColor = (s: string) => {
    const m: Record<string, string> = {
      new: '#3B82F6', quoted: '#7C3AED', lost: '#9CA3AF', pending: '#D97706',
      scheduling: '#6366F1', raw_material: '#D97706', vulcanizing: ORANGE,
      qc_passed: '#0891B2', packing: '#D97706', shipped: '#16A34A', completed: '#15803D',
    };
    return m[s] ?? '#5A6780';
  };

  return (
    <div className="space-y-6">
      {/* 标题 */}
      <div className="rounded-sm border border-border p-5" style={{ background: HEADER_BG }}>
        <h2 className="text-lg font-black tracking-wider" style={{ color: '#1A2337' }}>全链路流程追踪器</h2>
        <p className="text-xs mt-1" style={{ color: '#5A6780' }}>输入业务编号（PP-YYYYMMDD-NNN），查看从询盘→报价→生产→物流的完整历程与附件</p>
      </div>

      {/* 搜索栏 */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#5A6780' }} />
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && search()}
            placeholder="输入业务编号（如 PP-20260101-001）..."
            className="w-full pl-9 pr-4 py-2.5 rounded-sm text-sm outline-none"
            style={{ background: '#FFFFFF', border: '1px solid #D0D5E2', color: '#1A2337' }}
          />
        </div>
        <button onClick={search} disabled={loading}
          className="flex items-center gap-2 px-5 py-2.5 rounded-sm text-sm font-bold disabled:opacity-60"
          style={{ background: ORANGE, color: '#fff' }}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          追踪
        </button>
      </div>

      {/* 错误提示 */}
      {err && (
        <div className="flex items-center gap-2 p-4 rounded-sm border" style={{ background: '#FEF2F2', borderColor: '#FCA5A580' }}>
          <AlertCircle className="w-4 h-4 flex-shrink-0" style={{ color: '#EF4444' }} />
          <p className="text-sm" style={{ color: '#DC2626' }}>{err}</p>
        </div>
      )}

      {/* 结果 */}
      {result && (
        <div className="space-y-4">
          {/* 概览卡 — 以线索信息为主 */}
          <div className="rounded-sm border border-border p-5" style={{ background: CARD_BG }}>
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <p className="text-xl font-black font-mono" style={{ color: ORANGE }}>
                  {result.inquiry.biz_no ?? result.order?.order_no ?? '—'}
                </p>
                <p className="text-xs mt-1" style={{ color: '#5A6780' }}>
                  询盘时间：{new Date(result.inquiry.created_at).toLocaleString('zh-CN')}
                </p>
                <p className="text-xs mt-0.5" style={{ color: '#5A6780' }}>
                  {result.inquiry.name}{result.inquiry.company ? ` · ${result.inquiry.company}` : ''}
                  {result.inquiry.material ? ` | 产品：${result.inquiry.material}` : ''}
                </p>
              </div>
              <div className="flex gap-3 flex-wrap">
                <span className="text-xs font-bold px-3 py-1 rounded-sm border"
                  style={{ color: statusColor(result.inquiry.status), borderColor: `${statusColor(result.inquiry.status)}50`, background: `${statusColor(result.inquiry.status)}12` }}>
                  {ORDER_STATUS_LABEL[result.inquiry.status] ?? result.inquiry.status}
                </span>
                {result.order?.total_amount != null && (
                  <span className="text-xs font-bold px-3 py-1 rounded-sm" style={{ background: '#F0F2F5', color: '#1A2337' }}>
                    ¥ {Number(result.order.total_amount).toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
                  </span>
                )}
                {!result.order && (
                  <span className="text-xs px-3 py-1 rounded-sm border border-dashed" style={{ color: '#5A6780', borderColor: '#D0D5E2' }}>
                    尚未转单
                  </span>
                )}
              </div>
            </div>

            {/* 产品明细 */}
            {result.orderItems.length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-bold tracking-widest uppercase mb-2" style={{ color: '#5A6780' }}>产品明细</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr style={{ borderBottom: '1px solid #E5E7EB' }}>
                        {['材料/产品', '规格', '数量(kg)', '单价(元/kg)', '小计(元)'].map(h => (
                          <th key={h} className="text-left py-1.5 pr-4 font-mono" style={{ color: '#5A6780' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {result.orderItems.map(item => (
                        <tr key={item.id} style={{ borderBottom: '1px solid #F3F4F6' }}>
                          <td className="py-1.5 pr-4 font-medium" style={{ color: '#1A2337' }}>{item.material_code || '—'}</td>
                          <td className="py-1.5 pr-4" style={{ color: '#5A6780' }}>{item.specifications || '—'}</td>
                          <td className="py-1.5 pr-4 tabular-nums" style={{ color: '#1A2337' }}>{item.quantity_kg ?? '—'}</td>
                          <td className="py-1.5 pr-4 tabular-nums" style={{ color: '#1A2337' }}>{item.unit_price != null ? `¥${item.unit_price}` : '—'}</td>
                          <td className="py-1.5 tabular-nums font-bold" style={{ color: ORANGE }}>{item.total_amount != null ? `¥${Number(item.total_amount).toLocaleString()}` : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* 时间轴 */}
          <div className="rounded-sm border border-border p-5" style={{ background: CARD_BG }}>
            <p className="text-xs font-bold tracking-widest uppercase mb-5" style={{ color: '#5A6780' }}>全链路时间轴</p>

            {/* 节点1：询盘（必有） */}
            <TimelineNode
              icon={MessageSquare} color="#3B82F6"
              title={`客户询盘 · ${result.inquiry.name}${result.inquiry.company ? ` / ${result.inquiry.company}` : ''}`}
              subtitle={`感兴趣材料：${result.inquiry.material ?? '未填写'} · 编号：${result.inquiry.biz_no ?? '—'}`}
              time={result.inquiry.created_at}
              defaultOpen>
              <div className="space-y-1.5 text-xs">
                <p style={{ color: '#5A6780' }}>咨询内容：</p>
                <p style={{ color: '#1A2337' }}>{result.inquiry.message ?? '（无）'}</p>
                {result.inquiry.quoted_price != null && (
                  <p className="mt-2 font-bold" style={{ color: ORANGE }}>
                    已报价：¥ {Number(result.inquiry.quoted_price).toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
                  </p>
                )}
              </div>
            </TimelineNode>

            {/* 节点2：订单创建（有订单时） */}
            {result.order && (
            <TimelineNode
              icon={FileText} color={ORANGE}
              title={`订单创建 · ${result.order.order_no}`}
              subtitle={`状态：${ORDER_STATUS_LABEL[result.order.order_status] ?? result.order.order_status}`}
              time={result.order.created_at}>
              <p className="text-xs" style={{ color: '#1A2337' }}>
                总金额：{result.order.total_amount != null ? `¥ ${Number(result.order.total_amount).toLocaleString()}` : '待确认'}
              </p>
            </TimelineNode>
            )}

            {/* 节点3：生产任务（有订单时） */}
            {result.order && result.productionTasks.map((task, i) => {
              const sc = STAGE_LABEL[task.stage] ?? { label: task.stage, color: '#5A6780' };
              return (
                <TimelineNode key={task.id}
                  icon={Wrench} color={sc.color}
                  title={`生产节点 ${i + 1}：${sc.label}`}
                  subtitle={task.tech_notes ? `备注：${task.tech_notes.slice(0, 40)}…` : undefined}
                  time={task.updated_at}>
                  {task.tech_notes && <p className="text-xs" style={{ color: '#1A2337' }}>{task.tech_notes}</p>}
                </TimelineNode>
              );
            })}

            {/* 节点4：物流（有订单时） */}
            {result.order && result.logistics.map(lg => (
              <TimelineNode key={lg.id}
                icon={Truck} color="#0891B2"
                title={`物流发货 · ${lg.carrier ?? '运输商待填'}`}
                subtitle={lg.tracking_no ? `单号：${lg.tracking_no}` : '快递单号待录入'}
                time={lg.created_at}>
                <div className="text-xs space-y-1" style={{ color: '#1A2337' }}>
                  {lg.shipped_at && <p>发货时间：{new Date(lg.shipped_at).toLocaleString('zh-CN')}</p>}
                  {lg.estimated_arrival && <p>预计送达：{new Date(lg.estimated_arrival).toLocaleString('zh-CN')}</p>}
                </div>
              </TimelineNode>
            ))}

            {/* 末端节点（完成标志） */}
            <div className="flex gap-4">
              <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 border"
                style={{
                  background: result.inquiry.status === 'completed' ? '#DCFCE7' : '#F0F2F5',
                  borderColor: result.inquiry.status === 'completed' ? '#86EFAC' : '#D0D5E2',
                }}>
                <CheckCircle2 className="w-4 h-4"
                  style={{ color: result.inquiry.status === 'completed' ? '#16A34A' : '#9CA3AF' }} />
              </div>
              <div className="flex items-center">
                <p className="text-sm font-bold"
                  style={{ color: result.inquiry.status === 'completed' ? '#16A34A' : '#9CA3AF' }}>
                  {result.inquiry.status === 'completed' ? '订单已完成 ✓' : (result.order ? '等待完成…' : '线索尚未转单')}
                </p>
              </div>
            </div>
          </div>

          {/* 附件区（仅有订单时显示） */}
          {result.order && (
          <div className="rounded-sm border border-border p-5" style={{ background: CARD_BG }}>
            <div className="flex items-center gap-2 mb-4">
              <Paperclip className="w-4 h-4" style={{ color: ORANGE }} />
              <p className="text-xs font-bold tracking-widest uppercase" style={{ color: '#5A6780' }}>
                订单附件 ({result.attachments.length})
              </p>
            </div>

            {/* 已有附件列表 */}
            {result.attachments.length > 0 && (
              <div className="space-y-2 mb-4">
                {result.attachments.map(att => (
                  <div key={att.id} className="flex items-center justify-between gap-3 px-3 py-2 rounded-sm"
                    style={{ background: '#F7F8FA', border: '1px solid #D0D5E2' }}>
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-xs px-2 py-0.5 rounded-sm font-mono" style={{ background: `${ORANGE}20`, color: ORANGE }}>
                        {att.file_type}
                      </span>
                      <span className="text-xs truncate" style={{ color: '#1A2337' }}>{att.file_name}</span>
                      {att.file_size_kb && <span className="text-xs flex-shrink-0" style={{ color: '#5A6780' }}>{att.file_size_kb}KB</span>}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-xs hidden sm:block" style={{ color: '#5A6780' }}>
                        {new Date(att.created_at).toLocaleDateString('zh-CN')}
                      </span>
                      <a href={att.file_url} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-1 text-xs px-2 py-1 rounded-sm"
                        style={{ color: '#2563EB', background: '#EFF6FF' }}>
                        <ExternalLink className="w-3 h-3" /> 查看
                      </a>
                      <button
                        onClick={() => handleDeleteAttachment(att)}
                        disabled={deletingAttId === att.id}
                        className="flex items-center gap-1 text-xs px-2 py-1 rounded-sm disabled:opacity-60"
                        style={{ color: '#DC2626', background: '#FEF2F2' }}>
                        {deletingAttId === att.id
                          ? <Loader2 className="w-3 h-3 animate-spin" />
                          : <Trash2 className="w-3 h-3" />}
                        删除
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* 上传新附件 */}
            <div className="border-t border-border pt-4">
              <p className="text-xs font-bold mb-3" style={{ color: '#5A6780' }}>上传新附件</p>
              <AttachmentUploader orderId={result.order.id} onUploaded={reloadAttachments} />
            </div>
          </div>
          )}
        </div>
      )}

      {/* 空状态 */}
      {!result && !loading && !err && (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <Clock className="w-10 h-10" style={{ color: '#D0D5E2' }} />
          <p className="text-sm" style={{ color: '#9CA3AF' }}>输入业务编号开始追踪</p>
        </div>
      )}
    </div>
  );
}

export default TrackerView;